<template>
  <div class="candidate-list-container">
    <el-table
      ref="candidateListTable"
      class="candidate-list"
      row-key="id"
      tooltip-effect="light"
      height="100%"
      :default-sort="{prop: 'apply_time', order: 'descending'}"
      :data="data"
      :empty-text="$t('el.table.emptyText')"
    >
      <el-table-column min-width="150px" prop="candidate" :label="$t(`${i18nPrefix}.label.candidateName`)">
        <template #default="{ row }">
          <div
            class="avatar-name-container"
            :class="[row.candidate.is_employee && 'employee', row.candidate.is_alumni && 'alumni']"
          >
            <div class="avatar-container">
              <img class="avatar" :src="getCandidateAvatarPath(row)" @click="handleAvatarClick(row)"/>
            </div>
            <div class="name-container">
              <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="row.candidate.name">
                <div class="name" @click="handleNameClick(row)">{{ row.candidate.name }}</div>
              </el-tooltip>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column v-if="!fromJobPotential" show-overflow-tooltip min-width="165px" prop="job_info_list" :label="$t(`${i18nPrefix}.label.lastWorkJobName`)">
        <template #default="{ row }">
          <el-tooltip
            effect="light"
            popper-class="light-tooltip-popper"
            placement="bottom"
            :content="formatJobInfo(row.job, row.job_info_list)"
          >
            <el-select v-model="row.job" class="job-selector" :disabled="row.job_info_list.length === 1" :class="row.job_info_list.length > 1 ? '' : 'is-one-job'" @change="handelJobChange(row)">
              <el-option
                v-for="(job, index) in row.job_info_list"
                :key="index"
                :label="job.i18nName"
                :value="job.id"/>
            </el-select>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column min-width="92px" show-overflow-tooltip prop="location" :label="$t(`${i18nPrefix}.label.location`)">
        <template #default="{ row }">{{ row.candidate.locationInfo|empty }}</template>
      </el-table-column>
      <el-table-column min-width="208px" show-overflow-tooltip prop="email" :label="$t(`${i18nPrefix}.label.emailAddress`)">
        <template #default="{ row }">{{ row.candidate.email|empty }}</template>
      </el-table-column>
      <el-table-column v-if="hasCRMTagPermission" min-width="160px" prop="tags" class-name="tags" :label="$t(`${i18nPrefix}.label.tag`)">
        <template #default="{ row }">
          <CandidateListTagCellVue :tag-list="row.tags" />
        </template>
      </el-table-column>
      <el-table-column min-width="245px" :label="$t(`${i18nPrefix}.label.referralChain`)">
        <template #default="{ row }">
          <div v-if="!row.referral_chain">-</div>
          <div v-else class="referral-chain">
            <ReferralChain
              v-for="(item, index) in row.referral_chain"
              :key="index"
              :candidateId="item.rc_id"
              :name="item.name"
              :photo="item.profile_image_url"
              :type="item.type"
              :city="item.city"
              :isCandidate="item.is_candidate"
              :inTalentPool="item.in_talent_pool"
              :email="item.email"
              :alumni="item.is_alumni"
              :isTheLastOne="index === row.referral_chain.length - 1"
            />
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { getI18nPropertyValue } from '@/i18n'
import { REFERRAL_TYPE } from '@/constant/application'
import ReferralChain from './components/referralChain.vue'
import CandidateListTagCellVue from '@/components/candidate/candidateListTagCell.vue'

export default {
  components: {
    ReferralChain,
    CandidateListTagCellVue
  },
  props: {
    data: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    fromJobPotential: {
      type: Boolean,
      default: false
    }
  },
  metaInfo () {
    return {
      style: [
        {
          innerHTML: `.el-tooltip__popper.is-light {
            border-color: #e6e6e6;
            background: #e6e6e6;
            -webkit-box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
                    box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
          }
          .el-tooltip__popper.is-light[x-placement] .popper__arrow {
            border-bottom-color: #e6e6e6;
            border-top-color: #e6e6e6;
          }
          .el-tooltip__popper.is-light[x-placement] .popper__arrow::after {
            border-bottom-color: #e6e6e6;
            border-top-color: #e6e6e6;
          }`
        }
      ]
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      moment,
      getI18nPropertyValue,
      REFERRAL_TYPE,
      atBottom: false,
      emitScrollBottom: null
    }
  },
  computed: {
    hasCRMTagPermission () {
      return this.hasAnyPermission('PREMIUM_TAG_MANAGEMENT')
    }
  },
  created () {
    this.emitScrollBottom = _.debounce(() => {
      this.$emit('scrollBottom')
    }, 200)
  },
  mounted () {
    const tableBody = this.$el.querySelector('.el-table__body-wrapper')
    tableBody.addEventListener('scroll', () => {
      const distance = tableBody.scrollHeight - tableBody.scrollTop - tableBody.clientHeight - 10
      if (distance <= 50) {
        if (!this.atBottom) {
          this.emitScrollBottom()
        }
        this.atBottom = true
      } else {
        this.atBottom = false
      }
    })
  },
  methods: {
    formatJobInfo (selectedJobId, jobList) {
      const selectedJob = _.find(jobList, job => job.id === selectedJobId)
      return selectedJob?.i18nName
    },
    handleOperation (operation, row) {
      operation.operator(row)
    },
    handleNameClick (row) {
      this.$emit('openDetail', row)
    },
    handleAvatarClick (row) {
      this.$emit('openDetail', row)
    },
    handleCandidateStatusClick (row) {
      this.$emit('candidateStatusClick', row)
    },
    getCandidateAvatarPath (row) {
      return row.candidate.photo ? this.$DjangoBaseUrl(row.candidate.photo) : this.$DjangoStaticUrl('images_en/default.jpg')
    },
    scrollToTop () {
      const el = this.$el.querySelector('.el-table__body-wrapper')
      el.scrollTo({
        top: 0
      })
    },
    handelJobChange (rowData) {
      this.$emit('jobChange', rowData)
    }
  },
  watch: {
  }
}
</script>

<style lang="scss" scoped>
.candidate-list-container {
  position: relative;
  height: 100%;
  display: flex;
  transition: .3s all;

  &.expend {
    padding-right: 375px;
  }

  .candidate-list {
    .avatar-name-container {
      display: flex;
      align-items: center;

      &.is-new {
        .avatar-container {
          &::before {
            content: '';
            display: block;
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background-color: #ee7815;
            position: absolute;
            top: 0;
            left: 0;
          }
        }
      }

      &.employee {
        .avatar-container {
          .avatar {
            border-color: #0096cc;
          }
        }
      }

      &.alumni {
        .avatar-container {
          .avatar {
            border-color: #ee7815;
          }
        }
      }

      .avatar-container {
        position: relative;
        flex-shrink: 0;

        .avatar-lock-icon {
          position: absolute;
          bottom: 2px;
          right: 6px;
          font-size: 14px;
          color: #9e9e9e;
        }

        .avatar {
          display: block;
          width: 44px;
          height: 44px;
          padding: 1px;
          border: 1px solid transparent;
          border-radius: 50%;
          line-height: 1;
          cursor: pointer;
        }
      }

      .name-container {
        flex: 1;
        margin-left: 22px;
        overflow: hidden;

        .name {
          font-size: 15px;
          white-space: nowrap;
          word-break: keep-all;
          overflow: hidden;
          text-overflow: ellipsis;
          cursor: pointer;
        }
      }
    }

    .job-selector {
      ::v-deep .el-input__inner {
        border: 0;
        background-color: transparent;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 82%;
        padding: 0px;
      }
    }

    .is-one-job {
      ::v-deep {
        .el-input__inner {
          color: #232323;
          cursor: text;
        }
        .el-input__suffix {
          display: none;
        }
      }
    }

    .referral-chain {
      display: flex;
      overflow: auto;
      padding-top: 6px;

      &::-webkit-scrollbar {
        height: 6px;
      }

      &::-webkit-scrollbar-thumb{
        background-color: #0096CC;
        border: 0;
      }
    }

    ::v-deep {
      .cell {
        line-height: 24px;
      }

      .tags {
        padding: 7px 0;
      }

      .selection {
        .cell {
          padding: 0;
          text-align: center;
        }
      }
    }
  }
}
</style>
